import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, FormControl, IconButton, InputLabel,
  MenuItem, Select, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { buildingApi } from './BuildingApi';
import * as ETVConstants from '../ETVConstants';
import ETVActionButton from '../tools/ETVActionButton';
import ETVPage from '../tools/ETVPage';
import ETVPopup from '../tools/ETVPopup';
import ETVTable from '../tools/ETVTable';

export default function BuildingMessages() {
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const data = await buildingApi('admin_list_locations');
        const list = data.list || [];
        setLocations(list);
        if (list.length === 1) {
          const locationId = list[0].location_id;
          setSelectedId(locationId);
          const messages = await buildingApi('admin_list_messages', { query: { location_id: locationId } });
          setItems(messages.list || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load building messages');
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  const loadMessages = useCallback(async (locationId = selectedId) => {
    if (!locationId) { setItems([]); return; }
    setLoading(true);
    setError('');
    try {
      const data = await buildingApi('admin_list_messages', { query: { location_id: locationId } });
      setItems(data.list || []);
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  const chooseLocation = (id) => {
    setSelectedId(id);
    setSelected(null);
    setQrCode(null);
    loadMessages(id);
  };

  const loadBuildingQr = async () => {
    if (!selectedId) return;
    setQrLoading(true);
    setError('');
    try {
      const data = await buildingApi('admin_get_building_qr', { query: { location_id: selectedId } });
      setQrCode(data.qr_code || null);
    } catch (err) {
      setError(err.message || 'Failed to create QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQr = async () => {
    if (!qrCode?.image_url) return;
    try {
      const imageUrl = new URL(qrCode.image_url);
      imageUrl.searchParams.set('size', '1200');
      const response = await fetch(imageUrl.toString());
      if (!response.ok) throw new Error(`http_${response.status}`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      const building = locations.find(item => String(item.location_id) === String(selectedId));
      link.href = objectUrl;
      link.download = `${(building?.name || 'building').replace(/[^a-z0-9-_]+/gi, '-')}-messages-qr.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err.message || 'Failed to download QR code');
    }
  };

  const deleteMessage = async () => {
    if (!deleting) return;
    setLoading(true);
    setError('');
    try {
      await buildingApi('admin_delete_message', { method: 'POST', body: JSON.stringify(deleting) });
      setDeleting(null);
      setSelected(null);
      await loadMessages();
    } catch (err) {
      setError(err.message || 'Failed to delete message');
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    {
      title: 'Title',
      field: 'title',
      width: 180,
      render: item => <Typography variant="body2" fontWeight={700}>{item.title}</Typography>,
    },
    {
      title: 'Sender contact',
      field: 'sender_contact',
      width: 180,
      render: item => item.sender_contact || 'Anonymous',
    },
    {
      title: 'Message',
      field: 'message',
      render: item => (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.message}
        </Box>
      ),
    },
    {
      title: '',
      field: '',
      width: 88,
      cellStyle: { width: 88, paddingLeft: 2, paddingRight: 2, whiteSpace: 'nowrap' },
      headerStyle: { width: 88, paddingLeft: 2, paddingRight: 2 },
      render: item => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="View message">
            <IconButton size="small" color="primary" onClick={() => setSelected(item)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete message">
            <IconButton size="small" color="error" onClick={() => setDeleting(item)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]), []);

  const qrPreviewUrl = qrCode?.image_url
    ? `${qrCode.image_url}${qrCode.image_url.includes('?') ? '&' : '?'}size=512`
    : '';

  return (
    <ETVPage
      width="standard"
      icon={<ForumOutlinedIcon fontSize="large" />}
      title={ETVConstants.trans('received_building_messages') || 'Received building messages'}
      description={locations.length === 1
        ? 'View and manage the received messages for this building.'
        : 'Select a building to see and manage its received messages.'}
      loading={loading}
      topActions={selectedId ? (
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadMessages()}>
          Refresh messages
        </Button>
      ) : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {locations.length === 1 ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary">Building</Typography>
          <Typography variant="body1" fontWeight={600}>
            {locations[0].name} - {locations[0].address}
          </Typography>
        </Box>
      ) : (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="message-location-label">Building</InputLabel>
          <Select
            labelId="message-location-label"
            value={selectedId}
            label="Building"
            onChange={(event) => chooseLocation(event.target.value)}
          >
            {locations.map(item => (
              <MenuItem key={item.location_id} value={item.location_id}>{item.name} - {item.address}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedId && (
        <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            {ETVConstants.trans('building_message_qr_title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {ETVConstants.trans('building_message_qr_explainer')}
          </Typography>
          {!qrCode ? (
            <Button variant="contained" startIcon={<QrCode2Icon />} onClick={loadBuildingQr} disabled={qrLoading}>
              {qrLoading
                ? ETVConstants.trans('building_message_qr_loading')
                : ETVConstants.trans('building_message_qr_create')}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box
                component="img"
                src={qrPreviewUrl}
                alt="Building messaging QR code"
                sx={{ width: 200, height: 200, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
              />
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadQr}>
                {ETVConstants.trans('building_message_qr_download')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {selectedId && items.length > 0 && (
        <ETVTable
          data={items}
          columns={columns}
          title=""
          pageSize={10}
          search
          embedded
          options={{ padding: 'dense', tableLayout: 'fixed' }}
        />
      )}

      {selectedId && !loading && items.length === 0 && (
        <Alert severity="info">No messages received for this building.</Alert>
      )}

      <ETVPopup
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width="small"
        icon={<VisibilityIcon />}
        title={selected?.title || 'Message'}
        description={`Sender contact: ${selected?.sender_contact || 'Not provided'}`}
        bottomActions={(
          <ETVActionButton action="cancel" label="Close" onClick={() => setSelected(null)} />
        )}
      >
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selected?.message}</Typography>
      </ETVPopup>

      <ETVPopup
        open={Boolean(deleting)}
        onClose={() => !loading && setDeleting(null)}
        width="small"
        icon={<DeleteIcon color="error" />}
        title="Delete message?"
        description="Please confirm this permanent action."
        bottomActions={(
          <>
            <ETVActionButton action="cancel" disabled={loading} onClick={() => setDeleting(null)} />
            <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={deleteMessage} disabled={loading}>
              Delete message
            </Button>
          </>
        )}
      >
        <Typography>This permanently deletes “{deleting?.title}”.</Typography>
      </ETVPopup>
    </ETVPage>
  );
}
